import { useEffect, useState } from 'react';
import { PanelRightClose, Plus, Search } from 'lucide-react';
import { searchScratchpads } from '../../lib/scratchpads';
import type { Scratchpad } from '../../lib/scratchpads';

/** The server wraps hits in <mark>. Strip the tags and show plain text - the
    snippet is document content and must never be injected as HTML. */
function stripMarks(snippet: string): string {
  return snippet.replace(/<\/?mark>/g, '');
}

interface Props {
  pads: Scratchpad[];
  currentId: string;
  onSelect: (id: string) => void;
  onNewPage: () => void;
  collapsed: boolean;
  onToggleCollapsed: () => void;
}

export function PagesRail({ pads, currentId, onSelect, onNewPage, collapsed, onToggleCollapsed }: Props) {
  const [query, setQuery] = useState('');
  const [hitIds, setHitIds] = useState<Set<string>>(new Set());
  // Snippet per matching page, so the rail can show WHY a page matched. The
  // server builds these with <mark> tags; they are rendered as text, never as
  // HTML, so document content can never become a script vector.
  const [snippets, setSnippets] = useState<Record<string, string>>({});
  const needle = query.trim().toLowerCase();

  // Body search runs server-side against the FTS index; title matching stays
  // local so the rail filters instantly and still works if the request fails.
  useEffect(() => {
    if (!needle) { setHitIds(new Set()); setSnippets({}); return; }
    let cancelled = false;
    const timer = setTimeout(() => {
      searchScratchpads(needle)
        .then(hits => {
          if (cancelled) return;
          setHitIds(new Set(hits.map(h => h.id)));
          setSnippets(Object.fromEntries(
            hits.filter(h => h.snippet).map(h => [h.id, stripMarks(h.snippet)])
          ));
        })
        .catch(() => { if (!cancelled) { setHitIds(new Set()); setSnippets({}); } });
    }, 200);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [needle]);

  const matches = (p: Scratchpad) =>
    !needle || p.title.toLowerCase().includes(needle) || hitIds.has(p.id);

  // Collapsed means zero width, not a full-height bar. The affordance to
  // reopen is an edge tab rendered by the page, matching how the meeting
  // right panel behaves.
  return (
    <aside
      // One element, width-animated, so it slides rather than popping. A
      // conditional early-return renders a DIFFERENT element and React swaps
      // them outright, which no CSS transition can smooth.
      className={`flex-none bg-bg-secondary flex flex-col transition-all duration-200 ${
        collapsed ? 'w-0 overflow-hidden border-l-0' : 'w-56 border-l border-border'
      }`}
      aria-hidden={collapsed}
      inert={collapsed}
    >
      <div className="flex items-center justify-between px-3 py-2 border-b border-border">
        <span className="text-xs font-semibold uppercase tracking-wider text-text-muted">Pages</span>
        <button
          onClick={onToggleCollapsed}
          aria-label="Collapse pages"
          className="p-1 rounded-md text-text-muted hover:text-text-primary hover:bg-bg-hover"
        >
          <PanelRightClose size={16} />
        </button>
      </div>

      <div className="p-2 border-b border-border">
        <div className="flex items-center gap-1.5 px-2 py-1.5 rounded-md bg-bg-input">
          <Search size={13} className="text-text-muted" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search pages"
            aria-label="Search pages"
            className="flex-1 bg-transparent text-sm text-text-primary placeholder:text-text-muted focus:outline-none"
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-2 space-y-0.5">
        {pads.map((p) => (
          <button
            key={p.id}
            onClick={() => onSelect(p.id)}
            aria-current={p.id === currentId ? 'true' : undefined}
            data-match={matches(p) ? 'true' : 'false'}
            className={`w-full text-left px-2 py-1.5 rounded-md text-sm transition-colors
              ${p.id === currentId ? 'bg-bg-hover text-text-primary' : 'text-text-secondary hover:bg-bg-hover'}
              ${matches(p) ? '' : 'opacity-40'}`}
          >
            <span className="block truncate">{p.title}</span>
            {snippets[p.id] && (
              <span className="block truncate text-xs text-text-muted mt-0.5">{snippets[p.id]}</span>
            )}
          </button>
        ))}
      </div>

      <div className="p-2 border-t border-border">
        <button
          onClick={onNewPage}
          className="w-full flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-md border border-dashed border-border text-sm text-accent hover:bg-bg-hover"
        >
          <Plus size={14} /> New page
        </button>
      </div>
    </aside>
  );
}
